import React, { createContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { WebSocketContextValue, WebSocketEvent, WebSocketEventData, SocketType } from './WebSocketTypes';

// Create the WebSocket context with undefined as the initial value
const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

// WebSocket Provider props
interface WebSocketProviderProps {
  url?: string;
  autoConnect?: boolean;
  children: ReactNode;
}

// WebSocket Provider component
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  url = 'http://localhost:8080',
  autoConnect = false, // Temporarily disable auto-connect to prevent connection issues
  children
}) => {
  const [socket, setSocket] = useState<SocketType | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [lastError, setLastError] = useState<Error | null>(null);

  // Connect to WebSocket server
  const connect = useCallback((serverUrl = url, token?: string) => {
    console.log('[WebSocketContext] Connecting to:', serverUrl);
    if (socket) {
      socket.disconnect();
    }

    // Socket.IO is only needed after an explicit connection request. Keeping it
    // out of the startup graph avoids loading the transport stack for sessions
    // that never use realtime collaboration.
    void import('socket.io-client')
      .then(({ default: io }) => {
        const connectTimeout = setTimeout(() => {
          console.log('[WebSocketContext] Connection attempt timed out');
          setLastError(new Error('Connection timed out'));
        }, 3000);

        const newSocket = io(serverUrl, {
          auth: {
            ...(token ? { token } : {}),
            userId: 'demo-user-123',
            userName: 'Bianca Operations Lead'
          },
          reconnection: true,
          reconnectionAttempts: 2,
          reconnectionDelay: 1000,
          timeout: 3000,
          transports: ['websocket', 'polling']
        });

        newSocket.on('connect', () => {
          console.log('[WebSocketContext] WebSocket connected');
          setIsConnected(true);
          setLastError(null);
          clearTimeout(connectTimeout);
        });

        newSocket.on('connect_error', (error: Error) => {
          console.error('[WebSocketContext] WebSocket connection error:', error);
          setLastError(error);
          clearTimeout(connectTimeout);
        });

        newSocket.on('disconnect', (reason: string) => {
          console.log('[WebSocketContext] WebSocket disconnected:', reason);
          setIsConnected(false);
        });

        setSocket(newSocket);
      })
      .catch((error: unknown) => {
        console.error('[WebSocketContext] Error loading WebSocket client:', error);
        setLastError(error instanceof Error ? error : new Error(String(error)));
      });
  }, [socket, url]);

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  // Subscribe to an event
  const subscribe = <T extends WebSocketEventData>(event: WebSocketEvent, callback: (data: T) => void) => {
    if (socket) {
      socket.on(event, callback);
    }

    return () => {
      if (socket) {
        socket.off(event, callback);
      }
    };
  };

  // Emit an event
  const emit = <T extends WebSocketEventData>(event: WebSocketEvent, data: T) => {
    if (!socket || !isConnected) {
      console.warn('Cannot emit event, socket not connected:', event);
      return false;
    }

    socket.emit(event, data);
    return true;
  };

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, url, connect, disconnect]);

  // Context value
  const contextValue: WebSocketContextValue = {
    socket,
    isConnected,
    lastError,
    connect,
    disconnect,
    subscribe,
    emit
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
